import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dots } from 'assets/icons';
import DropdownMenu from 'components/DropdownMenu/DropdownMenu';
import UseContext from 'hooks/useContext';

const MENU_WIDTH = 224;

/**
 * The "…" of the invoice tab header, left of "+ Importer": it opens the import
 * journal of the month, errors or not (JBWEB-1280). Rendered in a portal, as
 * the header scrolls sideways and would clip it.
 */
const InvoiceHeaderMenu = ({ onOpenJournal }) => {
  const { language } = UseContext();
  const text = (key, fallback) =>
    language.getValue(`invoiceJournal.${key}`, fallback);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [isOpen]);

  const toggleMenu = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 4, left: rect.right - MENU_WIDTH });
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleMenu}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={text('moreActions', 'Plus d’actions')}
        className="flex items-center justify-center w-10 h-10 rounded-md border border-gray-200 hover:bg-gray-100"
      >
        <Dots w={14} h={4} color="#374151" />
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999]"
            style={{ top: position.top, left: position.left }}
          >
            <DropdownMenu
              width="w-56"
              items={[
                {
                  label: text('openJournal', 'Journal d’import'),
                  onClick: () => {
                    setIsOpen(false);
                    onOpenJournal();
                  },
                },
              ]}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

export default InvoiceHeaderMenu;
